import { Box, Card, CircularProgress, Typography } from "@mui/material";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { BudgetDashboardGroupItem } from "../../../types/dashboard";
import { ADOPTION_COLORS, formatPercent, getAdoptionColor } from "../utils";

interface AdoptionByCellChartProps {
  data: BudgetDashboardGroupItem[];
  isLoading: boolean;
}

const legendItems = [
  { color: ADOPTION_COLORS.good, label: "> 70%" },
  { color: ADOPTION_COLORS.warning, label: "40–70%" },
  { color: ADOPTION_COLORS.bad, label: "< 40%" },
];

export const AdoptionByCellChart = ({
  data,
  isLoading,
}: AdoptionByCellChartProps) => (
  <Card
    elevation={0}
    sx={{
      p: "18px 20px",
      height: "100%",
      boxSizing: "border-box",
      borderRadius: "12px",
      border: "1px solid #E4E7EC",
    }}
  >
    <Typography fontSize={15} fontWeight={700}>
      Adoção por Célula
    </Typography>
    <Typography color="#6B7686" fontSize={12.5}>
      Percentual de projetos com orçamento, por célula.
    </Typography>

    <Box display="flex" gap="16px" mt="8px" mb="12px" flexWrap="wrap">
      {legendItems.map((item) => (
        <Box key={item.label} display="flex" alignItems="center" gap={0.5}>
          <Box
            sx={{
              width: 10,
              height: 10,
              borderRadius: "3px",
              backgroundColor: item.color,
            }}
          />
          <Typography fontSize={12} color="#6B7686">
            {item.label}
          </Typography>
        </Box>
      ))}
    </Box>

    <Box height={340}>
      {isLoading ? (
        <Box
          height="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <CircularProgress />
        </Box>
      ) : !data.length ? (
        <Box
          height="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Typography color="#94A3B8">Sem dados no período.</Typography>
        </Box>
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={data}
            layout="vertical"
            margin={{ left: 8, right: 48 }}
          >
            <CartesianGrid strokeDasharray="3 3" horizontal={false} />
            <XAxis
              type="number"
              domain={[0, 100]}
              tickFormatter={(value) => `${value}%`}
            />
            <YAxis
              type="category"
              dataKey="name"
              width={124}
              tick={{ fontSize: 12 }}
            />
            <Tooltip
              formatter={(value: any, _name: string, entry: any) => [
                `${formatPercent(Number(value))} · ${
                  entry?.payload?.withBudget ?? 0
                } de ${entry?.payload?.totalProjects ?? 0} projeto(s)`,
                "Adoção",
              ]}
            />
            <Bar dataKey="adoptionRate" radius={[0, 4, 4, 0]} barSize={18}>
              {data.map((item) => (
                <Cell
                  key={item.id ?? item.name}
                  fill={getAdoptionColor(item.adoptionRate)}
                />
              ))}
              <LabelList
                dataKey="adoptionRate"
                position="right"
                formatter={(value: any) => formatPercent(Number(value))}
                style={{ fontSize: 11, fontWeight: 600, fill: "#475569" }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </Box>
  </Card>
);
